import type { Entitlements, Me } from '@cribbage/protocol';
import { useAuthStore } from './authStore.js';
import { DEFAULT_FINISH, FREE_BOARD_FINISHES, type ArchFinish } from './boardFinishes.js';
import { DEFAULT_CARD_BACK, FREE_CARD_BACKS, type CardBack } from './cardBacks.js';
import { DEFAULT_CARD_FRONT, FREE_CARD_FRONTS, type CardFront } from './cardFronts.js';
import { useSettingsStore } from './settingsStore.js';

/**
 * What a player's subscription grants on this device, and the styles the
 * table draws because of it.
 *
 * The server decides membership (`me.entitlements`); nothing here reads a
 * clock. A lapsed member's saved styles stay saved, on the device and on the
 * server, so they come back on resubscribing. Until then the table draws a
 * free style in place of a paid one (`brain/decisions.md` 2026-10-01).
 */

const FREE: Entitlements = { surface: false, comms: false, improver: false };

/**
 * The entitlements `/me` sent, with anything missing read as not granted. A
 * device can hold a `/me` from before the field existed, and there is no user
 * at all before `/me` answers (`docs/dev/subscriptions.md`, Gotchas).
 */
export function entitlementsOf(user: Me | null | undefined): Entitlements {
  const granted = (user as { entitlements?: Partial<Entitlements> | null } | null | undefined)
    ?.entitlements;
  if (granted == null) return FREE;
  return {
    surface: granted.surface === true,
    comms: granted.comms === true,
    improver: granted.improver === true,
  };
}

/** One entitlement of the signed-in player, for a component. */
export function useEntitlement(name: keyof Entitlements): boolean {
  return useAuthStore((state) => entitlementsOf(state.user)[name]);
}

/*
 * The style the table draws. A Game Surface member gets what they saved.
 * Anyone else gets what they saved when it is free, and the default when it is
 * not. Every default is free, so the fallback never lands on a paid style.
 */

export function drawnCardFront(saved: CardFront, member: boolean): CardFront {
  return member || FREE_CARD_FRONTS.has(saved) ? saved : DEFAULT_CARD_FRONT;
}

export function drawnCardBack(saved: CardBack, member: boolean): CardBack {
  return member || FREE_CARD_BACKS.has(saved) ? saved : DEFAULT_CARD_BACK;
}

export function drawnBoardFinish(saved: ArchFinish, member: boolean): ArchFinish {
  return member || FREE_BOARD_FINISHES.has(saved) ? saved : DEFAULT_FINISH;
}

/** The style settings, as the wire spells them, that hold a paid style. */
export function paidStyleKeys(styles: {
  readonly cardBack: CardBack;
  readonly cardFront: CardFront;
  readonly boardFinish: ArchFinish;
}): readonly string[] {
  return [
    ...(FREE_CARD_BACKS.has(styles.cardBack) ? [] : ['card_back']),
    ...(FREE_CARD_FRONTS.has(styles.cardFront) ? [] : ['card_front']),
    ...(FREE_BOARD_FINISHES.has(styles.boardFinish) ? [] : ['board_finish']),
  ];
}

/**
 * Whether the table may draw a saved paid style.
 *
 * No user yet is not the same as a player who is not a member. A member who
 * opens the app offline for a game against the bots keeps the styles they
 * paid for; only a `/me` that says otherwise swaps them for free ones.
 * Settings is stricter: it saves a paid style only when `/me` says the player
 * may use it.
 */
export function drawsPaidStyles(user: Me | null): boolean {
  return user === null || entitlementsOf(user).surface;
}

/** The art on every face-up card. */
export function useDrawnCardFront(): CardFront {
  const member = useAuthStore((state) => drawsPaidStyles(state.user));
  const saved = useSettingsStore((state) => state.cardFront);
  return drawnCardFront(saved, member);
}

/** The back every face-down card wears. */
export function useDrawnCardBack(): CardBack {
  const member = useAuthStore((state) => drawsPaidStyles(state.user));
  const saved = useSettingsStore((state) => state.cardBack);
  return drawnCardBack(saved, member);
}

/** The Classic board's finish. */
export function useDrawnBoardFinish(): ArchFinish {
  const member = useAuthStore((state) => drawsPaidStyles(state.user));
  const saved = useSettingsStore((state) => state.boardFinish);
  return drawnBoardFinish(saved, member);
}
